import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import theme from "../utils/theme";

const CustomerForm = forwardRef(function CustomerForm(props, ref) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    consultation: "",
    otp: "",
  });

  const [showOtp, setShowOtp] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(true);
  const [showConsult, setShowConsult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uId, setUId] = useState(""); // OTP 발송 시 받은 u_id 저장

  const formRef = useRef(null);
  useImperativeHandle(ref, () => ({
    scrollToForm: () => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    // 숫자만 입력 허용
    const numericValue = value.replace(/[^0-9]/g, '');
    // 11자리까지만 입력 허용
    if (numericValue.length <= 11) {
      setFormData(prev => ({ ...prev, phone: numericValue }));
    }
  };

  const handleSendOtp = async () => {
    if (formData.name && formData.name.trim() && formData.phone && formData.phone.length === 11) {
      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_CONNECTION_API_SERVER_URL}/sms/send/otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: formData.phone
          })
        });

        const result = await response.json();

        if (result.result_code === "0000") {
          setShowOtp(true);
          setUId(result.data.u_id); // u_id 저장
          alert("인증번호가 발송되었습니다.");
        } else {
          alert(`인증번호 발송 실패: ${result.result_message}`);
        }
      } catch (error) {
        console.error('OTP 발송 오류:', error);
        alert('인증번호 발송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAgreed) {
      alert("개인정보 처리 동의가 필요합니다.");
      return;
    }
    if (!formData.otp) {
      alert("인증번호를 입력해주세요.");
      return;
    }

    setIsLoading(true);
    try {
      // 1. OTP 인증
      const authResponse = await fetch(`${process.env.REACT_APP_CONNECTION_API_SERVER_URL}/auth/verify/otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          u_id: uId,
          otp: formData.otp
        })
      });

      const authResult = await authResponse.json();

      if (authResult.result_code === "0000") {
        // OTP 인증 성공 후 DB 입력 API 호출
        const userData = {
          user_name: formData.name,
          user_phone: formData.phone.startsWith('010') ? formData.phone : `010${formData.phone.replace(/-/g, "")}`,
          user_gender: "user_gender",
          user_salary: "user_salary",
          user_credit: "user_credit",
          birth_year: "birth_year",
          ad_name: "ad03",
          landing_code: "ad03",
          landing_url: "ad03.e-denden.com",
          landing_name: "ad03",
          agree: privacyAgreed ? "1" : "0",
          user_ip: "?",
          request_number: 1,
          user_message: formData.consultation && formData.consultation.replaceAll(" ", "") !== "" ? formData.consultation : "상담 요청드립니다.",
        };
        try {
          await fetch(`${process.env.REACT_APP_MARKETING_API_SERVER_URL}/ads/import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData)
          });
        } catch (e) {
          // DB 입력 실패 시에도 사용자에게는 성공 안내 (실서비스에서는 에러처리 필요)
          console.error('DB 저장 오류:', e);
        }
        alert("상담 신청이 완료되었어요! 이든파이낸셜의 전문 상담사가 빠른 시간 내에 연락드리겠습니다.");

        // Google Ads 전환 이벤트 발생
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-11087438780/J57pCL_4-ckaELzH86Yp'
          });
        }

        // 폼 초기화
        setFormData({
          name: "",
          phone: "",
          consultation: "",
          otp: "",
        });
        setShowOtp(false);
        setUId("");
      } else {
        alert(`인증번호 확인 실패: ${authResult.result_message}`);
      }
    } catch (error) {
      console.error('상담 신청 오류:', error);
      alert('상담 신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer ref={formRef}>
      <FormTitle>맞춤형 대출 솔루션을 찾아드려요!</FormTitle>
      <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="name">이름 *</Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="이름을 입력해주세요"
            required
            disabled={showOtp}
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="phone">연락처 *</Label>
          <Input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handlePhoneChange}
            placeholder="010-0000-0000 (번호만 입력)"
            maxLength="11"
            required
            disabled={showOtp}
          />
          {formData.name && formData.name.trim() && formData.phone && formData.phone.length === 11 && !showOtp && (
            <OtpButton
              type="button"
              onClick={handleSendOtp}
              disabled={isLoading}
            >
              {isLoading ? '발송중...' : '인증번호 받기'}
            </OtpButton>
          )}
        </InputGroup>

        {showOtp && (
          <InputGroup>
            <Label htmlFor="otp">OTP 인증번호 *</Label>
            <OtpContainer>
              <OtpInput
                type="text"
                id="otp"
                name="otp"
                value={formData.otp}
                onChange={handleInputChange}
                placeholder="인증번호 6자리"
                maxLength="6"
                required
              />
            </OtpContainer>
          </InputGroup>
        )}

        <ConsultToggle type="button" onClick={() => setShowConsult(v => !v)}>
          상담 내용(선택사항) {showConsult ? '▲' : '▼'}
        </ConsultToggle>
        {showConsult && (
          <InputGroup>
            <TextArea
              id="consultation"
              name="consultation"
              value={formData.consultation}
              onChange={handleInputChange}
              placeholder="원하시는 대출 조건이나 간단한 상황을 알려주시면 더 정확한 상담이 가능합니다. (선택사항)"
              rows="4"
            />
          </InputGroup>
        )}

        <PrivacySection>
          <PrivacyContent>
            <PrivacyLeft>
              <PrivacyTitle>개인정보 처리 동의</PrivacyTitle>
              <PrivacyList>
                <PrivacyItem>✓ 상담 목적으로만 사용</PrivacyItem>
                <PrivacyItem>✓ 상담 완료 후 완전히 파기</PrivacyItem>
              </PrivacyList>
            </PrivacyLeft>
            <ToggleContainer>
              <ToggleSwitch
                type="checkbox"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
              />
              <ToggleLabel>동의함</ToggleLabel>
            </ToggleContainer>
          </PrivacyContent>
        </PrivacySection>

        {showOtp && formData.otp && formData.otp.length >= 1 && (
          <SubmitButton
            type="submit"
            disabled={isLoading || formData.otp.length < 6}
            $isComplete={formData.otp.length === 6}
          >
            {isLoading ? '처리중...' : '번호인증 & 상담신청하기'}
          </SubmitButton>
        )}
      </Form>
    </FormContainer>
  );
});

export default CustomerForm;

const FormContainer = styled.section`
  width: 100%;
  padding: 24px 0;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
  max-width: 430px;
  margin: 0 auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid ${theme.gray20Color};
  border-radius: 16px;
`;

const Form = styled.form`
  margin: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.blackColor};
  margin-bottom: 2px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 16px;
  background: ${props => props.disabled ? '#f8f9fa' : 'white'};
  transition: all 0.3s ease;
  color: ${props => props.disabled ? '#6c757d' : 'inherit'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#e9ecef' : theme.mainColor};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px rgba(0, 123, 255, 0.1)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
  
  &:hover {
    border-color: ${props => props.disabled ? '#e9ecef' : '#adb5bd'};
  }
  
  &::placeholder {
    color: #adb5bd;
    font-weight: 400;
  }
`;



const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 16px;
  background: white;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.mainColor};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: #adb5bd;
  }
  
  &::placeholder {
    color: #adb5bd;
    font-weight: 400;
  }
`;

const PrivacySection = styled.div`
  background: linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%);
  border-radius: 10px;
  padding: 12px;
  margin: 6px 0;
  border: 1px solid #c3e6cb;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.1);
`;

const PrivacyTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #155724;
  margin-bottom: 0;
`;

const PrivacyContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`;

const PrivacyLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PrivacyList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PrivacyItem = styled.li`
  font-size: 12px;
  color: #155724;
  margin-bottom: 2px;
  line-height: 1.3;
  font-weight: 500;
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 60px;
  flex-shrink: 0;
`;

const ToggleSwitch = styled.input`
  appearance: none;
  width: 44px;
  height: 24px;
  background: #ccc;
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background 0.3s;
  
  &:checked {
    background: ${theme.mainColor};
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: 2px;
    transition: transform 0.3s;
  }
  
  &:checked:before {
    transform: translateX(20px);
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ToggleLabel = styled.span`
  font-size: 12px;
  color: ${theme.blackColor};
  font-weight: 500;
  text-align: center;
`;

const OtpContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const OtpInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 16px;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.mainColor};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: #adb5bd;
  }
  
  &::placeholder {
    color: #adb5bd;
    font-weight: 400;
  }
`;

const OtpButton = styled.button`
  // min-width: 150px;
  padding: 12px 16px;
  background: ${props => props.disabled ? '#ccc' : theme.mainColor};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  transition: all 0.3s ease;
  box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(0, 123, 255, 0.3)'};
  
  &:hover {
    background: ${props => props.disabled ? '#ccc' : '#0056b3'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 6px 16px rgba(0, 123, 255, 0.4)'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const SubmitButton = styled.button`
  padding: 16px 24px;
  background: ${props => {
    if (props.disabled) {
      return 'linear-gradient(135deg, #ccc 0%, #999 100%)';
    }
    if (props.$isComplete) {
      return 'linear-gradient(135deg, #28a745 0%, #20c997 100%)';
    }
    return `linear-gradient(135deg, ${theme.mainColor} 0%, #0056b3 100%)`;
  }};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 8px;
  transition: all 0.3s ease;
  box-shadow: ${props => {
    if (props.disabled) {
      return 'none';
    }
    if (props.$isComplete) {
      return '0 8px 24px rgba(40, 167, 69, 0.4)';
    }
    return '0 8px 24px rgba(0, 123, 255, 0.3)';
  }};
  
  &:hover {
    background: ${props => {
    if (props.disabled) {
      return 'linear-gradient(135deg, #ccc 0%, #999 100%)';
    }
    if (props.$isComplete) {
      return 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)';
    }
    return 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
  }};
    transform: ${props => props.disabled ? 'none' : 'translateY(-3px)'};
    box-shadow: ${props => {
    if (props.disabled) {
      return 'none';
    }
    if (props.$isComplete) {
      return '0 12px 32px rgba(40, 167, 69, 0.5)';
    }
    return '0 12px 32px rgba(0, 123, 255, 0.4)';
  }};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
`;

const ConsultToggle = styled.button`
  background: none;
  border: none;
  color: ${theme.blackColor};
  font-size: 15px;
  font-weight: 600;
  margin: 8px 0 0 0;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FormTitle = styled.h1`
  // font-size: 20px;
  font-weight: 700;
  color: rgb(238, 111, 14);
  text-align: center;
  margin-bottom: 20px;
  // letter-spacing: -1px;
`; 